import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import type { LightingSettings } from "../neon/types";

export interface SceneBundle {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  composer: EffectComposer;
  drawingPlane: THREE.Plane;
  /** Infinite picking plane mesh that accepts raycasts even without tubes. */
  drawingPlaneMesh: THREE.Mesh;
  wall: THREE.Mesh;
  setBackdrop: (id: BackdropId) => void;
  setLighting: (opts: LightingSettings) => void;
  resize: () => void;
}

export type BackdropId = "brick" | "dark" | "warm" | "none";

export const BACKDROPS: { id: BackdropId; label: string }[] = [
  { id: "brick", label: "Brick" },
  { id: "dark", label: "Dark wall" },
  { id: "warm", label: "Warm wall" },
  { id: "none", label: "None" },
];

export function createScene(canvas: HTMLCanvasElement): SceneBundle {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
    powerPreference: "high-performance",
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.85;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x07070b);
  scene.fog = new THREE.FogExp2(0x05050a, 0.035);

  const camera = new THREE.PerspectiveCamera(
    40,
    window.innerWidth / window.innerHeight,
    0.1,
    200,
  );
  camera.position.set(0, 0.5, 12);
  camera.lookAt(0, 0, 0);

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.minDistance = 4;
  controls.maxDistance = 30;
  controls.maxPolarAngle = Math.PI * 0.85;
  controls.target.set(0, 0, 0);
  controls.mouseButtons = {
    LEFT: THREE.MOUSE.ROTATE,
    MIDDLE: THREE.MOUSE.DOLLY,
    RIGHT: THREE.MOUSE.PAN,
  };

  // White ambient + a punchy white-ish rim so the inspector sliders map to
  // perceptible brightness, not a tinted dimmer that vanishes against the
  // already-dark wall material.
  const ambient = new THREE.AmbientLight(0xffffff, 0.35);
  scene.add(ambient);
  const rim = new THREE.DirectionalLight(0xb0c4ff, 0.6);
  rim.position.set(-4, 6, 4);
  scene.add(rim);

  const setLighting = (opts: LightingSettings): void => {
    ambient.intensity = opts.ambient;
    rim.intensity = opts.rim;
  };

  const wall = new THREE.Mesh(
    new THREE.PlaneGeometry(40, 22),
    makeBackdropMaterial("brick"),
  );
  wall.position.set(0, 0, -1.2);
  wall.name = "wall";
  scene.add(wall);

  const setBackdrop = (id: BackdropId): void => {
    (wall.material as THREE.Material).dispose();
    wall.material = makeBackdropMaterial(id);
    wall.visible = id !== "none";
    // Switch the clear color too so a transparent-ish backdrop doesn't show
    // a stark brick-era dark blue against the void.
    scene.background = id === "warm" ? new THREE.Color(0x12080a) : new THREE.Color(0x07070b);
  };

  const drawingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  const planeGeom = new THREE.PlaneGeometry(80, 80);
  const planeMat = new THREE.MeshBasicMaterial({
    visible: false,
    side: THREE.DoubleSide,
  });
  const drawingPlaneMesh = new THREE.Mesh(planeGeom, planeMat);
  drawingPlaneMesh.name = "drawing-plane";
  drawingPlaneMesh.userData.isDrawingPlane = true;
  scene.add(drawingPlaneMesh);

  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  const bloom = new UnrealBloomPass(
    new THREE.Vector2(window.innerWidth, window.innerHeight),
    0.7,
    0.55,
    0.55,
  );
  composer.addPass(bloom);
  composer.addPass(new OutputPass());

  const resize = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    renderer.setSize(w, h, false);
    composer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    bloom.setSize(w, h);
  };
  resize();
  window.addEventListener("resize", resize);

  return {
    renderer,
    scene,
    camera,
    controls,
    composer,
    drawingPlane,
    drawingPlaneMesh,
    wall,
    setBackdrop,
    setLighting,
    resize,
  };
}

function makeBackdropMaterial(id: BackdropId): THREE.Material {
  // Wall base colors are mid-grey rather than near-black so the ambient/rim
  // sliders have headroom to visibly brighten the room. They still read as
  // "dark" under the default intensities because the room lights start low.
  switch (id) {
    case "dark":
      return new THREE.MeshStandardMaterial({ color: 0x353540, roughness: 0.95, metalness: 0 });
    case "warm":
      return new THREE.MeshStandardMaterial({ color: 0x6b4032, roughness: 0.85, metalness: 0 });
    case "none":
      return new THREE.MeshBasicMaterial({ color: 0x000000, visible: false });
    case "brick":
    default:
      return new THREE.MeshStandardMaterial({
        map: brickTexture(),
        roughness: 0.95,
        metalness: 0.0,
        color: 0x4a4a58,
      });
  }
}

function brickTexture(): THREE.Texture {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = "#0d0d14";
  ctx.fillRect(0, 0, 512, 512);

  const brickW = 64;
  const brickH = 28;
  for (let y = 0; y < 512; y += brickH) {
    const offset = (y / brickH) % 2 === 0 ? 0 : brickW / 2;
    for (let x = -brickW; x < 512 + brickW; x += brickW) {
      const px = x + offset;
      const shade = 110 + Math.floor(Math.random() * 40);
      ctx.fillStyle = `rgb(${shade}, ${shade}, ${shade + 6})`;
      ctx.fillRect(px + 1, y + 1, brickW - 2, brickH - 2);
      ctx.fillStyle = "rgba(255,255,255,0.08)";
      ctx.fillRect(px + 1, y + 1, brickW - 2, 1);
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillRect(px + 1, y + brickH - 2, brickW - 2, 1);
    }
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(3, 2);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
